
import authStore from "@/store/auth";
import {defineComponent, onMounted, watch} from "vue";
import {LocationQuery, useRoute, useRouter} from 'vue-router';

export default defineComponent({
  name: 'LoginModal',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    initialTab: {
      type: String,
      required: false,
      default: 'login',
    },
  },
  data() {
    const {
      loginFormState,
      authByVk,
      signIn,
      signUp,
      initResetPassword,
      setNewPassword,
      clearErrorMessage,
      gRecaptchaKey
    } = authStore

    return {
      tab: this.initialTab,
      gRecaptchaKey,
      authByVk,
      signIn,
      signUp,
      initResetPassword,
      setNewPassword,
      loginFormState,
      captchaToken: "",
      clearErrorMessage,
      errors: {
        name: false,
        surname: false,
        username: false,
        password: false,
        confirmPassword: false,
        differentPasswords: false,
        invalidEmail: false,
        agreeTerms: false,
        agreeGettingAdvert: false,
      } as { [key: string]: boolean }
    }
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const {loginFormState, closeLoginModal} = authStore;

    const processQueryParams = (query: LocationQuery) => {
      const token = typeof query.token === "string" ? query.token : null;
      if (token) {
        console.log("processQueryParams", decodeURIComponent(token));
        loginFormState.newPasswordForm.token = decodeURIComponent(token);
      } else {
        loginFormState.newPasswordForm.token = "";
      }

      loginFormState.newPasswordForm.username = typeof query.username === "string"
          ? query.username
          : "";
      loginFormState.newPasswordForm.timestamp = typeof query.timestamp === "string"
          ? parseInt(query.timestamp, 10)
          : -1;
    };

    watch(
        () => route.query, // Наблюдаем за query-параметрами
        (newQuery) => {
          processQueryParams(newQuery);
        },
        {immediate: true} // Вызывается сразу при загрузке
    );

    onMounted(() => {
      processQueryParams(route.query);
    });

    return {router, route, closeLoginModal};
  },
  watch: {
    // Следим за изменением маршрута и обновляем вкладку
    'route.query.tab': {
      immediate: true,
      handler(newTab) {
        this.tab = newTab || 'login'; // Если таб не указан, по умолчанию 'login'
      },
    },
  },
  methods: {
    closeLogin() {
      this.closeLoginModal();
      // Убираем параметры из URL при закрытии
      const {slm, tab, hash, token, timestamp, username, ...restQuery} = this.route.query;
      this.router.replace({query: {...restQuery}});
    },
    clearFieldError(fields: string[]) {
      fields.forEach((field) => {
        this.errors[field] = false; // Убираем ошибку для каждого указанного поля
      });
    },
    validateEmail(email: string): boolean {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    validateSignInForm() {
      this.errors.username = !this.loginFormState.form.username;
      this.errors.password = !this.loginFormState.form.password;

      return !this.errors.username && !this.errors.password;
    },
    validateSignUpForm() {
      this.errors.name = !this.loginFormState.form.name;
      this.errors.surname = !this.loginFormState.form.surname;

      this.errors.username = !this.loginFormState.form.username;
      this.errors.invalidEmail = this.loginFormState.form.username != null && !this.validateEmail(this.loginFormState.form.username);

      // Проверка пароля на все условия
      const isPasswordValid =
          this.loginFormState.form.password &&
          this.loginFormState.form.password.length >= 8 && // Длина
          /\d/.test(this.loginFormState.form.password) && // Цифры
          /[A-Z]/.test(this.loginFormState.form.password); // Заглавные буквы

      this.errors.password = !isPasswordValid;

      this.errors.confirmPassword = !this.loginFormState.form.confirmPassword;

      this.errors.differentPasswords = !!(this.loginFormState.form.confirmPassword
          && this.loginFormState.form.confirmPassword != this.loginFormState.form.password);

      return !this.errors.name
          && !this.errors.surname
          && !this.errors.username
          && !this.errors.invalidEmail
          && !this.errors.password
          && !this.errors.confirmPassword
          && !this.errors.differentPasswords;
    },
    validateInitResetPasswordForm() {
      this.errors.username = !this.loginFormState.form.username;
      this.errors.invalidEmail = this.loginFormState.form.username != null && !this.validateEmail(this.loginFormState.form.username);

      return !this.errors.username
          && !this.errors.invalidEmail;
    },
    validateSetNewPasswordForm() {
      const isPasswordValid =
          this.loginFormState.newPasswordForm.password &&
          this.loginFormState.newPasswordForm.password.length >= 8 && // Длина
          /\d/.test(this.loginFormState.newPasswordForm.password) && // Цифры
          /[A-Z]/.test(this.loginFormState.newPasswordForm.password); // Заглавные буквы

      this.errors.password = !isPasswordValid;

      this.errors.confirmPassword = !this.loginFormState.newPasswordForm.confirmPassword;

      this.errors.differentPasswords = !!(this.loginFormState.newPasswordForm.confirmPassword
          && this.loginFormState.newPasswordForm.confirmPassword != this.loginFormState.newPasswordForm.password);

      return !this.errors.password
          && !this.errors.confirmPassword
          && !this.errors.differentPasswords;
    },
    validateRegByVk() {
      console.log("validateRegByVk")
      this.errors.agreeTerms = !this.loginFormState.form.agreeTerms;

      if (this.errors.agreeTerms) {
        this.loginFormState.errorMessage = "Необходимо принять условия соглашения"
      }
      return !this.errors.agreeTerms;
    },

    async handleAuthByVk() {
      await this.authByVk(this.closeLogin);
    },

    async handleRegByVk() {
      if (!this.validateRegByVk()) {
        return;
      }
      await this.authByVk(this.closeLogin);
    },

    async handleSignIn() {
      if (!this.validateSignInForm()) {
        return;
      }

      await grecaptcha.ready(async () => {
        this.captchaToken = await grecaptcha.execute(this.gRecaptchaKey, {
          action: "login",
        });

        await this.signIn(this.captchaToken);
      });
    },

    async handleSignUp() {
      if (!this.validateSignUpForm()) {
        return;
      }

      await grecaptcha.ready(async () => {
        this.captchaToken = await grecaptcha.execute(this.gRecaptchaKey, {
          action: "reg",
        });

        await this.signUp(this.captchaToken);
      });
    },

    async handleInitResetPassword() {
      if (!this.validateInitResetPasswordForm()) {
        return;
      }

      await grecaptcha.ready(async () => {
        this.captchaToken = await grecaptcha.execute(this.gRecaptchaKey, {
          action: "initResetPassword",
        });

        await this.initResetPassword(this.captchaToken, () => {
          this.changeTab("waitpassres")
        });
      });
    },

    async handleSetNewPassword() {
      if (!this.validateSetNewPasswordForm()) {
        return;
      }

      await grecaptcha.ready(async () => {
        this.captchaToken = await grecaptcha.execute(this.gRecaptchaKey, {
          action: "setNewPassword",
        });

        await this.setNewPassword(this.captchaToken, () => {
          this.changeTab("login")
        });
      });
    },

    changeTab(tabName: string) {
      this.loginFormState.errorMessage = ""

      // Сбрасываем все ошибки
      Object.keys(this.errors).forEach((key) => {
        this.errors[key] = false;
      });

      this.router.push({
        query: {
          ...this.route.query,
          slm: this.isVisible + "",
          tab: tabName
        }
      });
    },
  }
})
